import clsx from "clsx";
import { FC } from "react";
import { useTranslation } from "react-i18next";

import { FreeEsimTrialTooltip } from "@/components/Onboarding/Steps/RateSelection/FreeEsimTrialTooltip/FreeEsimTrialTooltip";
import { OptionContainer } from "@/components/Onboarding/Steps/RateSelection/OptionContainer/OptionContainer";
import { RateRadioButton } from "@/components/Onboarding/Steps/RateSelection/RateRadioButton/RateRadioButton";
import { RateOptionProps } from "@/types";

export const RateOptionDesktop: FC<RateOptionProps> = ({
  name,
  yearOption,
  monthOption,
  preSelectedProcessedProduct,
  isSelected,
  currentProductId,
  readonlyProductId,
  shouldShowEsimTrial = false,
}) => {
  const { t } = useTranslation();

  const isRecommended = yearOption?.recommended || monthOption?.recommended;
  const yearIsPreselected =
    yearOption?.id === preSelectedProcessedProduct?.id ||
    yearOption?.voice === preSelectedProcessedProduct?.id;
  const monthIsPreselected =
    monthOption?.id === preSelectedProcessedProduct?.id ||
    monthOption?.voice === preSelectedProcessedProduct?.id;
  const isIdPreselected = yearIsPreselected || monthIsPreselected;
  const isPrecharged = preSelectedProcessedProduct?.precharged;

  return (
    <OptionContainer
      selected={isSelected}
      recommended={isRecommended}
      // Row is disabled if we have a precharged option and neither the year nor month option is that precharged option.
      disabled={isPrecharged && !isIdPreselected}
      precharged={isPrecharged && isIdPreselected}
    >
      <div className="descriptions" data-testid="rate-descriptions">
        <span className="font-semibold text-primary-100 transition-shadow">
          {name}
        </span>
        <span className="speed-label">
          {monthOption?.bandwidthLabel} Mbit/s
        </span>
      </div>
      <div className="durations">
        <div
          data-testid="rate-durations"
          className={clsx(
            "duration",
            isPrecharged && !monthIsPreselected && "disabled",
          )}
        >
          {monthOption && (
            <RateRadioButton
              isReadonly={readonlyProductId === monthOption?.id}
              transformedProduct={monthOption}
              disabled={isPrecharged && !monthIsPreselected}
              preSelectedProcessedProduct={preSelectedProcessedProduct}
            />
          )}
        </div>
        <div
          className={clsx(
            "duration",
            isPrecharged && !yearIsPreselected && "disabled",
          )}
        >
          {yearOption && (
            <RateRadioButton
              isReadonly={readonlyProductId === yearOption?.id}
              transformedProduct={yearOption}
              disabled={isPrecharged && !yearIsPreselected}
              preSelectedProcessedProduct={preSelectedProcessedProduct}
            />
          )}
        </div>
        {shouldShowEsimTrial &&
          (currentProductId === yearOption.id ||
            currentProductId === monthOption.id) && (
            <p className="font-x-small col-span-2 mt-4 flex whitespace-break-spaces">
              <span className="mr-2">
                {t("Onboarding.sections.rate-selection.esim-trial-hint")}
              </span>
              <FreeEsimTrialTooltip leftOnMobile={true} />
            </p>
          )}
      </div>
    </OptionContainer>
  );
};
